import type { FeedItem, LikeResponse, PaginatedFeedResponse, PaginatedLikersResponse } from '@/types/feed';
import type { FeedItemWire, LikeResponseWire, PaginatedFeedResponseWire, PaginatedLikersResponseWire } from '../schemas/feed';

/**
 * Mapping functions to convert feed wire format (backend) to domain format (app)
 */

/**
 * Convert a single feed item from wire format to domain format
 */
export function mapFeedItemWireToDomain(wire: FeedItemWire): FeedItem {
  return {
    checkIn: {
      id: wire.check_in.id,
      userId: wire.check_in.user_id,
      businessId: wire.check_in.business_id,
      menuItemId: wire.check_in.menu_item_id,
      ratingId: wire.check_in.rating_id,
      status: wire.check_in.status,
      diningType: wire.check_in.dining_type,
      createdAt: wire.check_in.created_at,
      occurredAt: wire.check_in.occurred_at,
      businessName: wire.check_in.business_name,
      menuItemName: wire.check_in.menu_item_name,
    },
    user: {
      id: wire.user.id,
      username: wire.user.username,
      displayName: wire.user.display_name,
      avatarUrl: wire.user.avatar_url,
    },
    menuItem: {
      id: wire.menu_item.id,
      name: wire.menu_item.name,
      businessId: wire.menu_item.business_id,
      businessName: wire.menu_item.business_name,
    },
    rating: wire.rating
      ? {
          id: wire.rating.id,
          key: wire.rating.key,
          label: wire.rating.label,
        }
      : null,
    ratingDistribution: wire.rating_distribution
      ? {
          notForMe: wire.rating_distribution.not_for_me,
          okay: wire.rating_distribution.okay,
          tryIt: wire.rating_distribution.try_it,
          mustTry: wire.rating_distribution.must_try,
        }
      : null,
    photos: wire.photos.map((p) => ({
      id: p.id,
      imageUrl: p.image_url,
      ...(p.r2_key ? { r2Key: p.r2_key } : {}),
    })),
    review: wire.review
      ? {
          id: wire.review.id,
          content: wire.review.content,
          createdAt: wire.review.created_at,
        }
      : null,
    likesCount: wire.likes_count,
    commentsCount: wire.comments_count,
    userHasLiked: wire.user_has_liked,
    recentLikers: (wire.recent_likers ?? []).map((l) => ({
      id: l.id,
      username: l.username,
      displayName: l.display_name,
      avatarUrl: l.avatar_url,
    })),
    listSaveCount: wire.user_list_save_count,
    recentComments: (wire.recent_comments ?? []).map((c) => ({
      id: c.id,
      checkInId: c.check_in_id,
      userId: c.user_id,
      content: c.content,
      createdAt: c.created_at,
      updatedAt: c.updated_at,
      user: {
        id: c.user.id,
        username: c.user.username,
        displayName: c.user.display_name,
        avatarUrl: c.user.avatar_url,
      },
    })),
  };
}

/**
 * Convert paginated feed response from wire format to domain format
 */
export function mapPaginatedFeedWireToDomain(
  wire: PaginatedFeedResponseWire
): PaginatedFeedResponse {
  return {
    items: wire.items.map(mapFeedItemWireToDomain),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}

/**
 * Convert like response from wire format to domain format
 */
export function mapLikeResponseWireToDomain(wire: LikeResponseWire): LikeResponse {
  return {
    liked: wire.liked,
    likesCount: wire.likes_count,
    recentLikers: (wire.recent_likers ?? []).map((l) => ({
      id: l.id,
      username: l.username,
      displayName: l.display_name,
      avatarUrl: l.avatar_url,
    })),
  };
}

/**
 * Convert paginated likers response from wire format to domain format
 */
export function mapPaginatedLikersWireToDomain(
  wire: PaginatedLikersResponseWire
): PaginatedLikersResponse {
  return {
    items: wire.items.map((l) => ({
      id: l.id,
      username: l.username,
      displayName: l.display_name,
      avatarUrl: l.avatar_url,
      likedAt: l.liked_at,
    })),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}
